// @ts-check
// Cards que o próprio membro acabou de passar para uma etapa de Pode passar (contrato de equipe, seção 3.2): o card
// sai da visão dele, e a página ou o painel que estavam abertos mostram "Card passado para {etapa}" com o caminho de
// volta à lista, em vez do 404 genérico de registro inexistente. Vale enquanto a aba estiver aberta.
import { signal } from '@preact/signals';

// só os últimos repasses importam: é o que a pessoa ainda pode ter aberto ou no histórico do navegador
const MAX_HANDED_OFF_RECORDS = 50;

/** @typedef {'loading' | 'ready' | 'notFound' | 'handedOff'} RecordShowStatus */

/** Chave `<objeto>:<id>` para o rótulo da etapa de destino. */
export const handedOffRecordsState = signal(/** @type {Map<string, string>} */ (new Map()));

/** @param {string} objectNameSingular @param {string} recordId */
const keyOf = (objectNameSingular, recordId) => `${objectNameSingular}:${recordId}`;

/**
 * Lista de repasses com mais um no fim (o mais antigo sai quando passa do limite).
 * @param {Map<string, string>} records @param {string} key @param {string} stageLabel
 * @returns {Map<string, string>}
 */
export function addHandedOffRecord(records, key, stageLabel) {
  const next = new Map(records);
  next.delete(key);
  next.set(key, stageLabel);
  while (next.size > MAX_HANDED_OFF_RECORDS) next.delete(/** @type {string} */ (next.keys().next().value));
  return next;
}

/** @param {string} objectNameSingular @param {string} recordId @param {string} stageLabel rótulo já traduzido */
export function markRecordHandedOff(objectNameSingular, recordId, stageLabel) {
  handedOffRecordsState.value = addHandedOffRecord(handedOffRecordsState.value, keyOf(objectNameSingular, recordId), stageLabel);
}

/** O card voltou a ser lido (outra pessoa o trouxe de volta para uma etapa visível). */
export function clearRecordHandedOff(/** @type {string} */ objectNameSingular, /** @type {string} */ recordId) {
  const key = keyOf(objectNameSingular, recordId);
  if (!handedOffRecordsState.value.has(key)) return;
  const next = new Map(handedOffRecordsState.value);
  next.delete(key);
  handedOffRecordsState.value = next;
}

/**
 * Rótulo da etapa para onde o membro passou o registro, ou null.
 * @param {string} objectNameSingular @param {string} recordId
 */
export function readHandedOffStage(objectNameSingular, recordId) {
  return handedOffRecordsState.value.get(keyOf(objectNameSingular, recordId)) ?? null;
}

/**
 * Estado da página ou do painel do registro: sem o registro depois de carregado (o record-store o tirou ao passar o
 * card) ou com 404 ao reabrir, o card passado pelo próprio membro mostra o estado de repasse; os demais, não encontrado.
 * @param {'loading' | 'ready' | 'notFound'} status @param {boolean} hasRecord @param {string | null} handedOffStage
 * @returns {RecordShowStatus}
 */
export function resolveRecordShowStatus(status, hasRecord, handedOffStage) {
  if (status === 'loading' || hasRecord) return status;
  if (handedOffStage !== null) return 'handedOff';
  return 'notFound';
}

/**
 * Para onde o botão "Voltar" do card passado leva: a lista de onde o card foi aberto (view, filtros e coluna ficam no
 * endereço) ou, sem ela, a lista do objeto.
 * @param {{ objectNameSingular: string, recordIds: string[], listPath?: string | null } | null | undefined} context
 * @param {{ nameSingular: string, namePlural: string }} objectMetadata @param {string} recordId
 */
export function resolveHandedOffBackPath(context, objectMetadata, recordId) {
  const isOrigin = context?.objectNameSingular === objectMetadata.nameSingular && context.recordIds.includes(recordId);
  return isOrigin && context?.listPath ? context.listPath : `/objects/${objectMetadata.namePlural}`;
}
